import { ZfBarChart, ZfStack, ZfTypography } from '@zafire/react';
import type Highcharts from 'highcharts';
import type { PointStateValue } from 'highcharts';
import type { TFunction } from 'i18next';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LiquidityCalculatorMetricsDto } from '../../../app/api/generated/client';
import { chartAssetColors } from '../../../app/components/wealth/charts/pieChartOptions';
import { formatAmountCurrency } from '../../../app/utils/renderUtils';

// Inverted stack: segment length is `shapeArgs.height`, not `width`.
const MIN_SEGMENT_LENGTH_FOR_DATA_LABEL_PX = 90;

// Opacity applied to the non-hovered segments and legend items to highlight the hovered one.
const HOVER_DIM_OPACITY = 0.75;

type ChartSegmentId = 'totalCashInternal' | 'totalCashExternal' | 'longTermInvestments';

interface ILiquidityOverviewBarChartProps {
    metrics?: LiquidityCalculatorMetricsDto;
    currency: string;
}

const SEGMENT_ORDER: { id: ChartSegmentId; colorIndex: number }[] = [
    { id: 'totalCashInternal', colorIndex: 0 },
    { id: 'totalCashExternal', colorIndex: 1 },
    { id: 'longTermInvestments', colorIndex: 3 },
];

const SEGMENT_LABEL_KEYS: Record<ChartSegmentId, string> = {
    totalCashInternal: 'liquidityOverviewChart.label.cashJB',
    totalCashExternal: 'liquidityOverviewChart.label.cashOtherBanks',
    longTermInvestments: 'liquidityOverviewChart.label.longTermInvestments',
};

function buildLiquidityBarLegendItems(t: TFunction): { key: ChartSegmentId; color: string; label: string }[] {
    return SEGMENT_ORDER.map(({ id, colorIndex }) => ({
        key: id,
        color: chartAssetColors[colorIndex],
        label: t(SEGMENT_LABEL_KEYS[id]),
    }));
}

export const LiquidityOverviewBarChart: FunctionComponent<ILiquidityOverviewBarChartProps> = ({ metrics, currency }) => {
    const { t } = useTranslation();

    const chartRef = useRef<Highcharts.Chart | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [hoveredKey, setHoveredKey] = useState<ChartSegmentId | null>(null);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const captureChart = (event: Event) => {
            chartRef.current = (event as CustomEvent<Highcharts.Chart>).detail ?? null;
        };

        wrapper.addEventListener('chartInit', captureChart);

        return () => wrapper.removeEventListener('chartInit', captureChart);
    }, []);

    const highlightChartSegment = useCallback((key: ChartSegmentId | null) => {
        if (!chartRef.current) return;

        chartRef.current.series.forEach((chartSeries) => {
            const point = chartSeries.points?.[0];
            if (!point) return;

            const state: PointStateValue = key == null ? 'normal' : chartSeries.userOptions.id === key ? 'hover' : 'inactive';
            point.setState(state);
        });
    }, []);

    const handleLegendEnter = useCallback(
        (key: ChartSegmentId) => {
            setHoveredKey(key);
            highlightChartSegment(key);
        },
        [highlightChartSegment],
    );

    const handleLegendLeave = useCallback(() => {
        setHoveredKey(null);
        highlightChartSegment(null);
    }, [highlightChartSegment]);

    const internal = metrics?.totalCashJb ?? 0;
    const external = metrics?.totalCashOtherBanks ?? 0;
    const longTerm = metrics?.longTermInvestments ?? 0;
    const total = internal + external + longTerm;
    const axisMax = Math.max(total, 1);

    const legendItems = useMemo(() => buildLiquidityBarLegendItems(t), [t]);

    const series = useMemo<Highcharts.SeriesOptionsType[]>(() => {
        const values: Record<ChartSegmentId, number> = {
            totalCashInternal: internal,
            totalCashExternal: external,
            longTermInvestments: longTerm,
        };

        return SEGMENT_ORDER.map(({ id, colorIndex }) => ({
            type: 'column',
            id,
            name: t(SEGMENT_LABEL_KEYS[id]),
            stack: 'liquidity',
            color: chartAssetColors[colorIndex],
            states: {
                hover: { opacity: 1, brightness: 0, halo: null },
                inactive: { opacity: HOVER_DIM_OPACITY },
            },
            point: {
                events: {
                    mouseOver: () => setHoveredKey(id),
                    mouseOut: () => setHoveredKey(null),
                },
            },
            data: [values[id] > 0 ? values[id] : null],
        }));
    }, [external, internal, longTerm, t]);

    const options = useMemo<Highcharts.Options>(
        () => ({
            chart: {
                type: 'column',
                inverted: true,
                backgroundColor: 'transparent',
                height: 75,
                width: null,
                spacing: [18, 12, 18, 12],
            },
            exporting: { enabled: false },
            title: { text: undefined },
            tooltip: {
                enabled: true,
                backgroundColor: '#ffffff',
                borderWidth: 0,
                borderRadius: 4,
                // eslint-disable-next-line react-hooks/unsupported-syntax -- Highcharts dataLabel formatter API
                formatter: function (this) {
                    const y = this.y as number;

                    return `<span style="color: var(--fg-color-default-emphasis); font-size: var(--type-label-small-font-size); line-height: var(--type-label-small-line-height);">${this.series.name}</span><br/><span style="color: var(--fg-color-default-normal); font-size: var(--type-body-small-font-size); line-height: var(--type-body-small-line-height);"><b>${formatAmountCurrency(y, currency)}</b></span>`;
                },
            },
            legend: { enabled: false },
            xAxis: {
                visible: false,
            },
            yAxis: {
                min: 0,
                max: axisMax,
                visible: false,
                reversedStacks: false,
            },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    animation: false,
                    clip: false,
                    events: {
                        legendItemClick: () => false,
                    },
                },
                column: {
                    borderWidth: 0,
                    clip: false,
                    borderRadius: {
                        radius: 6,
                        scope: 'stack',
                        where: 'all',
                    },
                    pointWidth: 52,
                    dataLabels: {
                        enabled: true,
                        inside: true,
                        align: 'center',
                        verticalAlign: 'middle',
                        allowOverlap: false,
                        crop: true,
                        style: {
                            fontSize: '12px',
                            fontWeight: '600',
                            textOutline: '1px contrast',
                        },
                        formatter: function (this) {
                            const y = this.y as number;
                            if (!y) return '';

                            const stackLengthPx = this.series.chart.plotWidth ?? 0;
                            const segmentLength = this.shapeArgs?.height ?? (total > 0 ? (y / total) * stackLengthPx : 0);

                            if (segmentLength < MIN_SEGMENT_LENGTH_FOR_DATA_LABEL_PX) return '';

                            return formatAmountCurrency(y, currency);
                        },
                    },
                },
            },
            series,
        }),
        [axisMax, currency, series, total],
    );

    return (
        <ZfStack direction="column" alignment="center" spacing="extra-small">
            <div ref={wrapperRef} style={{ width: '75%', overflow: 'visible' }}>
                <ZfBarChart options={options} />
            </div>
            <ZfStack direction="row" justify="center" spacing="medium" style={{ flexWrap: 'wrap' }}>
                {legendItems.map((item) => (
                    <ZfStack
                        key={item.key}
                        direction="row"
                        alignment="center"
                        spacing="extra-extra-small"
                        onMouseEnter={() => handleLegendEnter(item.key)}
                        onMouseLeave={handleLegendLeave}
                        style={{
                            opacity: hoveredKey != null && hoveredKey !== item.key ? HOVER_DIM_OPACITY : 1,
                            transition: 'opacity 150ms ease-in-out',
                            cursor: 'default',
                        }}
                    >
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                backgroundColor: item.color,
                                flexShrink: 0,
                            }}
                        />
                        <ZfTypography variant="label-medium" color="normal">
                            {item.label}
                        </ZfTypography>
                    </ZfStack>
                ))}
            </ZfStack>
        </ZfStack>
    );
};
